import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { useIsOpenWithData } from '@shared/hooks/useIsOpenWithData';
import { reportAppError } from '@shared/reportAppError';
import { CENTS_IN_DOLLAR } from '@utils/currency';
import { useRestaurant } from '../../../context/useRestaurant';
import {
  createListing,
  type CreateListingPayload,
  getListing,
  type ListingWarnings,
  type NewListing,
} from '../apiHelpers';
import { useListingsContext } from '../ListingsContext';
import type { ListingFormData } from '../types';
import { getDefaultFormValues } from '../utils/formHelpers';
import { getListingPagePath } from '../utils/listingUtils';

export const useCreateListing = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id: restaurantId } = useRestaurant();
  const {
    floorPlans,
    refreshFloorPlans,
    refreshListings,
    selectedFloorPlan,
    setSelectedFloorPlanId,
    setSelectedListingId,
  } = useListingsContext();

  const { data: duplicateListing, isPending } = useAbortEffect(
    async (signal) => {
      if (state.duplicateListingId) {
        return getListing(state.duplicateListingId, restaurantId, signal);
      }
      return undefined;
    },
    [restaurantId, state.duplicateListingId],
  );

  const formFloorPlan = duplicateListing
    ? floorPlans.find((fp) => fp.id === duplicateListing.floorPlanId)!
    : selectedFloorPlan;

  const { control, handleSubmit, setValue, resetField } =
    useForm<ListingFormData>({
      values: getDefaultFormValues({
        floorPlan: formFloorPlan,
        isListingLoading: isPending,
        listing: duplicateListing || null,
        isDuplicating: !!state.duplicateListingId,
      }),
    });

  const {
    isOpen: isErrorModalOpen,
    data: errorModalData,
    open: openErrorModal,
    close: closeErrorModal,
  } = useIsOpenWithData<{ message: string }>();
  const {
    isOpen: isWarningModalOpen,
    data: warningModalData,
    open: openWarningModal,
    close: closeWarningModal,
  } = useIsOpenWithData<{ warnings: string[] }>();

  const buildPayload = ({
    data,
    ignoreWarnings,
  }: {
    data: ListingFormData;
    ignoreWarnings: boolean;
  }): CreateListingPayload => ({
    floorPlanId: data.floorPlanId,
    highlightedFloorPlanTableIds: data.highlightedTables.map(
      (table) => table.id,
    ),
    iconName: data.iconName,
    maximumGuests: Number(data.maximumGuests),
    minimumGuests: Number(data.minimumGuests),
    name: data.name,
    price: Number(data.price) * CENTS_IN_DOLLAR,
    publicName: data.publicName,
    turnTime: data.turnTime,
    shiftIds: data.shiftIds,
    ignoreWarnings,
  });

  const handleOnClickPublish = handleSubmit(async (data: ListingFormData) => {
    let response: ListingWarnings | NewListing;

    try {
      response = await createListing(
        restaurantId,
        buildPayload({ data, ignoreWarnings: false }),
      );

      if ('warnings' in response) {
        openWarningModal({ warnings: response.warnings });
      } else {
        successToast({
          message: 'Listing successfully published',
        });
        refreshListings();
        refreshFloorPlans();
        setSelectedFloorPlanId(data.floorPlanId);
        setSelectedListingId(response.createdListingId);
        navigate(getListingPagePath('published'));
      }
    } catch (e) {
      if (e instanceof ApiError) {
        openErrorModal({ message: e.message });
      }
      reportAppError(e);
    }
  });

  const handleOnConfirmSubmit = handleSubmit(async (data: ListingFormData) => {
    try {
      const response = await createListing(
        restaurantId,
        buildPayload({ data, ignoreWarnings: true }),
      );

      successToast({
        message: `Listing successfully published`,
      });
      refreshListings();
      refreshFloorPlans();
      setSelectedFloorPlanId(data.floorPlanId);
      setSelectedListingId((response as NewListing).createdListingId);
      navigate(getListingPagePath('published'));
    } catch (e) {
      if (e instanceof ApiError) {
        openErrorModal({ message: e.message });
      }
      reportAppError(e);
    }
  });

  return {
    closeErrorModal,
    closeWarningModal,
    control,
    errorModalData,
    floorPlans,
    handleOnClickPublish,
    handleOnConfirmSubmit,
    isErrorModalOpen,
    isWarningModalOpen,
    referrer: state.referrer,
    warningModalData,
    setValue,
    resetField,
  };
};
